import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, Heart } from "lucide-react";

const API = `${
  process.env.REACT_APP_BACKEND_URL ?? "https://comicverseaiapp.onrender.com"
}/api`;

export default function SharedComicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/shared/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [token]);

  if (error)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-md mx-auto p-12 text-center">
          <div className="cv-border cv-shadow-lg rounded-2xl bg-yellow-100 p-8">
            <div className="text-5xl mb-3">🤔</div>
            <h1 className="font-headings uppercase text-3xl mb-2">
              Comic not found
            </h1>
            <p className="font-body font-bold">
              The link may be broken or the comic was removed.
            </p>
          </div>
        </div>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="p-12 text-center font-body font-bold">Loading...</div>
      </div>
    );

  const comic = data.comic;
  const panels = comic.panels || [];
  const currentPanel = panels[page];

  return (
    <div className="min-h-screen halftone-pink">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8">
        {data.recipient_name && (
          <div
            className="cv-border cv-shadow rounded-2xl bg-yellow-300 p-6 mb-6 text-center"
            data-testid="shared-greeting"
          >
            <div className="font-comic text-2xl">A comic for</div>
            <div className="font-headings uppercase text-4xl my-1">
              {data.recipient_name}!
            </div>
            {data.message && (
              <div className="speech-bubble mt-4 inline-block max-w-md">
                {data.message}
              </div>
            )}
          </div>
        )}
        <h1
          className="font-headings uppercase text-4xl mb-2 text-center"
          data-testid="shared-title"
        >
          {comic.title}
        </h1>
        <p className="text-center font-body font-bold text-black/70 mb-6">
          A comic shared with love{" "}
          <Heart size={14} className="inline fill-pink-500 stroke-black" />
        </p>

        {currentPanel && (
          <div className="cv-border cv-shadow-lg rounded-2xl bg-white overflow-hidden">
            <div className="aspect-[4/3] bg-zinc-100 relative border-b-4 border-black flex items-center justify-center">
              {currentPanel.image_url ? (
                <img
                  src={currentPanel.image_url}
                  alt={`Panel ${page + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="halftone-purple absolute inset-0 flex items-center justify-center">
                  <p className="font-comic text-3xl">Drawing...</p>
                </div>
              )}
              {currentPanel.sfx && (
                <div
                  className="absolute top-6 right-6 burst font-comic text-3xl"
                  style={{ background: "#FBBF24", padding: "20px 28px" }}
                >
                  {currentPanel.sfx}
                </div>
              )}
              {currentPanel.narration && (
                <div className="absolute bottom-0 left-0 right-0 border-t-4 border-black bg-yellow-200 px-4 py-2 font-body font-bold text-sm text-center">
                  {currentPanel.narration}
                </div>
              )}
              {currentPanel.dialogue && (
                <div
                  className="absolute top-4 speech-bubble max-w-[60%]"
                  style={{ fontFamily: comic.font }}
                >
                  {currentPanel.dialogue}
                </div>
              )}
            </div>
            <div className="p-4 flex items-center justify-between">
              <Button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="bg-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
              >
                <ChevronLeft /> Prev
              </Button>
              <span className="font-headings text-xl">
                Panel {page + 1} / {panels.length}
              </span>
              <Button
                onClick={() =>
                  setPage((p) => Math.min(panels.length - 1, p + 1))
                }
                disabled={page === panels.length - 1}
                className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
              >
                Next <ChevronRight />
              </Button>
            </div>
          </div>
        )}

        <div className="text-center mt-8">
          <p className="font-body font-bold text-sm mb-3">
            Want to make your own?
          </p>
          <a href="/signup">
            <Button className="bg-yellow-400 hover:bg-yellow-300 text-black cv-border cv-shadow cv-press font-headings text-xl uppercase rounded-xl px-8 py-5">
              Try ComicVerse free
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
